import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, X } from "lucide-react";

const getVisitorId = () => {
  let id = localStorage.getItem("gm_visitor_id");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("gm_visitor_id", id);
  }
  return id;
};

const EMAIL_TO = "hello@gentleandmonster.com";

const HOME_OPTIONS = [
  { id: "project", label: "I have a project" },
  { id: "audit", label: "I'd like an audit" },
  { id: "talk", label: "Talk to Alex or Antoine" },
  { id: "booking", label: "Book a meeting" },
  { id: "message", label: "Leave a message" },
];

const PROJECT_TYPES = ["Meta Ads", "Creative Strategy", "UGC & AI Content", "I'm not sure yet"];

const FLOWS = {
  project: {
    title: "Tell us briefly about your project.",
    subject: "G&M Concierge — Project",
    fields: [
      { key: "name", label: "Name", required: true },
      { key: "brand", label: "Brand / Company", required: true },
      { key: "email", label: "Email", type: "email", required: true },
      { key: "phone", label: "Phone / WhatsApp", optional: true },
      { key: "web", label: "Website or Instagram", optional: true },
      { key: "message", label: "Message", textarea: true, required: true },
    ],
  },
  audit: {
    title: "Tell us briefly what you'd like us to look at.",
    subject: "G&M Concierge — Audit request",
    fields: [
      { key: "name", label: "Name", required: true },
      { key: "brand", label: "Brand", required: true },
      { key: "web", label: "Website or Instagram", required: true },
      { key: "email", label: "Email", type: "email", required: true },
      { key: "message", label: "Message", textarea: true, required: true },
    ],
  },
  talk: {
    title: "They're not available in live chat right now, but I can take a message for you.",
    subject: "G&M Concierge — Message for",
    fields: [
      { key: "name", label: "Name", required: true },
      { key: "email", label: "Email", type: "email", required: true },
      { key: "message", label: "Message", textarea: true, required: true },
    ],
  },
  booking: {
    title: "Let's find a time to talk.",
    subject: "G&M Concierge — Meeting request",
    fields: [
      { key: "name", label: "Name", required: true },
      { key: "email", label: "Email", type: "email", required: true },
      { key: "brand", label: "Brand / Company", required: true },
      { key: "time", label: "Preferred day / time", required: true },
    ],
  },
  message: {
    title: "Leave us a message.",
    subject: "G&M Concierge — Message",
    fields: [
      { key: "name", label: "Name", required: true },
      { key: "email", label: "Email", type: "email", required: true },
      { key: "message", label: "Message", textarea: true, required: true },
    ],
  },
};

const inputCls =
  "w-full border-b border-[#27272A] bg-transparent py-2.5 font-monoalt text-xs uppercase tracking-[0.12em] text-white placeholder:text-zinc-400 transition-colors duration-300 focus:border-white focus:outline-none";

const stepMotion = {
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
  transition: { duration: 0.35, ease: [0.16, 1, 0.3, 1] },
};

function OptionRow({ label, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="group flex w-full items-center justify-between border-b border-[#27272A] py-4 text-left font-monoalt text-[11px] uppercase tracking-[0.2em] text-zinc-400 transition-colors duration-300 hover:bg-white hover:px-3 hover:text-black"
    >
      {label}
      <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
    </button>
  );
}

export default function Concierge({ open, onClose }) {
  const [step, setStep] = useState("home");
  const [history, setHistory] = useState([]);
  const [flow, setFlow] = useState(null);
  const [person, setPerson] = useState(null);
  const [projectType, setProjectType] = useState(null);
  const [data, setData] = useState({});
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [sending, setSending] = useState(false);
  const [presence, setPresence] = useState({ alex: "AWAY", antoine: "AWAY" });
  const listRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    let stop = false;
    const load = async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/concierge/presence`, {
          cache: "no-store",
        });
        if (!r.ok) return;
        const d = await r.json();
        if (!stop) setPresence({ alex: d.alex || "AWAY", antoine: d.antoine || "AWAY" });
      } catch {
        /* presence stays at fallback */
      }
    };
    load();
    const t = setInterval(load, 60000);
    return () => {
      stop = true;
      clearInterval(t);
    };
  }, [open]);

  const refreshHistory = async () => {
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/concierge/messages?external_id=${getVisitorId()}`,
        { cache: "no-store" }
      );
      if (!r.ok) return;
      const d = await r.json();
      setMessages(d.messages || []);
    } catch {
      /* history refresh is best-effort */
    }
  };

  useEffect(() => {
    if (!open || step !== "chat") return;
    let stopped = false;
    const ctrl = new AbortController();
    const run = async () => {
      while (!stopped) {
        try {
          const r = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/concierge/stream?external_id=${getVisitorId()}`,
            { signal: ctrl.signal }
          );
          const reader = r.body.getReader();
          const dec = new TextDecoder();
          let buf = "";
          while (!stopped) {
            const { value, done } = await reader.read();
            if (done) break;
            buf += dec.decode(value, { stream: true });
            const parts = buf.split("\n\n");
            buf = parts.pop();
            for (const p of parts) {
              const line = p.split("\n").find((l) => l.startsWith("data: "));
              if (!line) continue;
              try {
                const m = JSON.parse(line.slice(6));
                setMessages((prev) =>
                  prev.some((x) => String(x.message_id) === String(m.message_id))
                    ? prev
                    : [...prev, m]
                );
              } catch {
                /* ignore malformed events */
              }
            }
          }
        } catch {
          /* reconnect below */
        }
        if (!stopped) {
          await refreshHistory();
          await new Promise((r) => setTimeout(r, 2000));
        }
      }
    };
    refreshHistory();
    run();
    const fallback = setInterval(refreshHistory, 12000);
    return () => {
      stopped = true;
      ctrl.abort();
      clearInterval(fallback);
    };
  }, [open, step]);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      setStep("home");
      setHistory([]);
      setFlow(null);
      setPerson(null);
      setProjectType(null);
      setData({});
      setMessages([]);
      setChatInput("");
    }
  }, [open]);

  const go = (next) => {
    setHistory((h) => [...h, step]);
    setStep(next);
  };

  const back = () => {
    setHistory((h) => {
      const prev = h[h.length - 1] || "home";
      setStep(prev);
      return h.slice(0, -1);
    });
  };

  const startFlow = (id) => {
    setFlow(id);
    setData({});
    if (id === "project") go("project-type");
    else if (id === "talk") go("talk-who");
    else go("form");
  };

  const submit = async () => {
    const f = FLOWS[flow];
    if (flow === "talk") {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/concierge/talk`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            external_id: getVisitorId(),
            name: data.name,
            email: data.email,
            person,
            message: data.message,
          }),
        });
        if (!r.ok) throw new Error("chat error");
      } catch {
        setHistory((h) => [...h, step]);
        setStep("error");
        return;
      }
      setHistory((h) => [...h, step]);
      await refreshHistory();
      setStep("chat");
      return;
    }
    if (flow !== "talk") {
      const lines = f.fields
        .map((fd) => `${fd.label}: ${data[fd.key] || "—"}`)
        .join("\n");
      const extra = flow === "project" ? `Looking for: ${projectType}` : null;
      const subject = f.subject;
      const body = `${extra ? extra + "\n\n" : ""}${lines}`;
      window.location.href = `mailto:${EMAIL_TO}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    }
    setHistory((h) => [...h, step]);
    setStep("thanks");
  };

  const set = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));

  const presenceLabel = (p) => {
    const s = presence[(p || "").toLowerCase()] || "AWAY";
    if (s === "AVAILABLE") return { dot: "●", text: "AVAILABLE NOW", cls: "text-white" };
    if (s === "OFFLINE") return { dot: "○", text: "OFFLINE", cls: "text-zinc-600" };
    return { dot: "●", text: "AWAY", cls: "text-zinc-400" };
  };

  const talkTitle = () => {
    const s = presence[(person || "").toLowerCase()] || "AWAY";
    if (s === "AVAILABLE") return `${person} is available right now — say hi.`;
    if (s === "OFFLINE") return `${person} is offline right now, but I can take a message for you.`;
    return `${person} is away right now, but I can take a message for you.`;
  };

  const sendFollowup = async (e) => {
    e.preventDefault();
    const content = chatInput.trim();
    if (!content || sending) return;
    setSending(true);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/concierge/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ external_id: getVisitorId(), content }),
      });
      if (!r.ok) throw new Error("send error");
      setChatInput("");
      await refreshHistory();
    } catch {
      /* keep the typed message so nothing is lost */
    } finally {
      setSending(false);
    }
  };

  const heads = (
    <div className="flex items-center gap-3" data-testid="concierge-heads">
      <img src="/assets/logo-duo.png" alt="Gentle and Monster" className="h-9 w-auto" />
      <span className="font-monoalt text-[10px] uppercase tracking-[0.3em] text-zinc-400">
        G&amp;M Concierge
      </span>
    </div>
  );

  const content = (
    <AnimatePresence>
      {open && (
        <div key="concierge" className="fixed inset-0 z-[70]">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/60 lg:bg-transparent"
            data-testid="concierge-backdrop"
          />
          <motion.aside
            initial={{ opacity: 0, y: -14, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.98 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-x-4 bottom-4 top-[84px] flex flex-col overflow-hidden border border-[#27272A] bg-black sm:bottom-auto sm:left-auto sm:right-6 sm:max-h-[calc(100vh-120px)] sm:w-[400px] lg:right-14 lg:top-[92px]"
            data-testid="concierge-panel"
          >
            <div className="flex items-center justify-between border-b border-[#27272A] px-6 py-4">
              {heads}
              <button
                type="button"
                onClick={onClose}
                data-testid="concierge-close"
                aria-label="Close concierge"
                className="flex h-8 w-8 items-center justify-center text-zinc-400 transition-colors duration-300 hover:text-white"
              >
                <X className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </div>

            <div className={`flex-1 px-6 py-6 ${step === "chat" ? "flex flex-col overflow-hidden" : "overflow-y-auto"}`}>
              <AnimatePresence mode="wait">
                {step === "home" && (
                  <motion.div key="home" {...stepMotion}>
                    <p className="font-display text-xl font-bold uppercase leading-tight tracking-tight">
                      Hi! Welcome to Gentle &amp; Monster.
                    </p>
                    <p className="mt-3 text-sm text-zinc-400">How can I help you today?</p>
                    <div className="mt-6 border-t border-[#27272A]">
                      {HOME_OPTIONS.map((o) => (
                        <OptionRow
                          key={o.id}
                          label={`${o.label} →`}
                          testid={`concierge-option-${o.id}`}
                          onClick={() => startFlow(o.id)}
                        />
                      ))}
                    </div>
                  </motion.div>
                )}

                {step === "project-type" && (
                  <motion.div key="project-type" {...stepMotion}>
                    <p className="font-display text-xl font-bold uppercase leading-tight tracking-tight">
                      What are you looking for?
                    </p>
                    <div className="mt-6 border-t border-[#27272A]">
                      {PROJECT_TYPES.map((t, i) => (
                        <OptionRow
                          key={t}
                          label={t}
                          testid={`concierge-project-type-${i}`}
                          onClick={() => {
                            setProjectType(t);
                            go("form");
                          }}
                        />
                      ))}
                    </div>
                  </motion.div>
                )}

                {step === "talk-who" && (
                  <motion.div key="talk-who" {...stepMotion}>
                    <p className="font-display text-xl font-bold uppercase leading-tight tracking-tight">
                      Who would you like to talk to?
                    </p>
                    <div className="mt-6 border-t border-[#27272A]">
                      {["Alex", "Antoine"].map((p) => {
                        const st = presenceLabel(p);
                        return (
                          <button
                            key={p}
                            type="button"
                            data-testid={`concierge-person-${p.toLowerCase()}`}
                            onClick={() => {
                              setPerson(p);
                              go("form");
                            }}
                            className="group flex w-full items-center justify-between border-b border-[#27272A] py-4 text-left font-monoalt text-[11px] uppercase tracking-[0.2em] text-zinc-400 transition-colors duration-300 hover:bg-white hover:px-3 hover:text-black"
                          >
                            <span className="flex items-center gap-3">
                              {p} <span className="text-zinc-400">→</span>
                            </span>
                            <span
                              data-testid={`concierge-presence-${p.toLowerCase()}`}
                              className={`font-monoalt text-[9px] uppercase tracking-[0.25em] ${st.cls} group-hover:text-zinc-700`}
                            >
                              {st.dot} {st.text}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </motion.div>
                )}

                {step === "form" && flow && (
                  <motion.form
                    key={`form-${flow}`}
                    {...stepMotion}
                    onSubmit={(e) => {
                      e.preventDefault();
                      submit();
                    }}
                  >
                    <p className="font-display text-lg font-bold uppercase leading-tight tracking-tight">
                      {flow === "talk" ? talkTitle() : FLOWS[flow].title}
                    </p>
                    <div className="mt-6 space-y-5">
                      {FLOWS[flow].fields.map((fd) => (
                        <label key={fd.key} className="block" data-testid={`concierge-field-${fd.key}`}>
                          <span className="font-monoalt text-[9px] uppercase tracking-[0.25em] text-zinc-400">
                            {fd.label}
                            {fd.optional ? " — optional" : ""}
                          </span>
                          {fd.textarea ? (
                            <textarea
                              rows={3}
                              value={data[fd.key] || ""}
                              onChange={set(fd.key)}
                              required={fd.required}
                              className={`${inputCls} resize-none normal-case tracking-normal`}
                            />
                          ) : (
                            <input
                              type={fd.type || "text"}
                              value={data[fd.key] || ""}
                              onChange={set(fd.key)}
                              required={fd.required}
                              className={inputCls}
                            />
                          )}
                        </label>
                      ))}
                    </div>
                    <button
                      type="submit"
                      data-testid="concierge-submit"
                      className="group mt-8 flex w-full items-center justify-between border border-white/40 px-5 py-3.5 font-monoalt text-[11px] uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:bg-white hover:text-black"
                    >
                      Send message
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                  </motion.form>
                )}

                {step === "chat" && (
                  <motion.div key="chat" {...stepMotion} className="flex h-full flex-col" data-testid="concierge-chat">
                    <p className="font-monoalt text-[9px] uppercase tracking-[0.25em] text-zinc-400">
                      Conversation with {person}
                    </p>
                    <div
                      ref={listRef}
                      className="mt-4 flex-1 space-y-5 overflow-y-auto pr-1"
                      data-testid="concierge-message-list"
                    >
                      {messages.map((m, i) => (
                        <div
                          key={String(m.message_id)}
                          data-testid={`concierge-msg-${i}`}
                          className={m.role === "visitor" ? "text-right" : ""}
                        >
                          <p className="font-monoalt text-[8px] uppercase tracking-[0.25em] text-zinc-400">
                            {m.role === "agent" ? m.sender : "You"}
                          </p>
                          <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-zinc-400">
                            {m.content}
                          </p>
                        </div>
                      ))}
                    </div>
                    <form
                      onSubmit={sendFollowup}
                      className="mt-4 flex items-end gap-3 border-t border-[#27272A] pt-4"
                    >
                      <input
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        placeholder="Write a message…"
                        data-testid="concierge-chat-input"
                        className={`${inputCls} normal-case tracking-normal`}
                      />
                      <button
                        type="submit"
                        disabled={sending || !chatInput.trim()}
                        data-testid="concierge-chat-send"
                        aria-label="Send message"
                        className="flex h-10 w-10 shrink-0 items-center justify-center border border-white/40 text-white transition-colors duration-300 hover:bg-white hover:text-black disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-white"
                      >
                        <ArrowRight className="h-4 w-4" />
                      </button>
                    </form>
                  </motion.div>
                )}

                {step === "error" && (
                  <motion.div key="error" {...stepMotion} data-testid="concierge-error">
                    {heads}
                    <p className="mt-6 font-display text-xl font-bold uppercase leading-tight tracking-tight">
                      Something went wrong.
                    </p>
                    <p className="mt-3 text-sm leading-relaxed text-zinc-400">
                      Your message could not be sent right now. Please reach us directly at{" "}
                      {EMAIL_TO}.
                    </p>
                    <div className="mt-6 border-t border-[#27272A]">
                      <OptionRow
                        label="Email us directly →"
                        testid="concierge-error-mailto"
                        onClick={() => {
                          window.location.href = `mailto:${EMAIL_TO}?subject=${encodeURIComponent("G&M Concierge — Message for " + (person || "the studio"))}`;
                        }}
                      />
                    </div>
                  </motion.div>
                )}

                {step === "thanks" && (
                  <motion.div key="thanks" {...stepMotion} data-testid="concierge-thanks">
                    {heads}
                    <p className="mt-6 font-display text-xl font-bold uppercase leading-tight tracking-tight">
                      {flow === "talk" ? "Message sent." : "Message ready."}
                    </p>
                    <p className="mt-3 text-sm leading-relaxed text-zinc-400">
                      {flow === "talk"
                        ? `${person} has been notified and will get back to you as soon as possible.`
                        : `Your email draft has been prepared for ${EMAIL_TO}. Send it and we'll get back to you shortly.`}
                    </p>
                    <div className="mt-6 border-t border-[#27272A]">
                      <OptionRow
                        label="Back to start →"
                        testid="concierge-restart"
                        onClick={() => {
                          setHistory([]);
                          setStep("home");
                        }}
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {step !== "home" && (
              <div className="border-t border-[#27272A] px-6 py-3">
                <button
                  type="button"
                  onClick={back}
                  data-testid="concierge-back"
                  className="flex items-center gap-2 font-monoalt text-[10px] uppercase tracking-[0.25em] text-zinc-400 transition-colors duration-300 hover:text-white"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Back
                </button>
              </div>
            )}
          </motion.aside>
        </div>
      )}
    </AnimatePresence>
  );

  return createPortal(content, document.body);
}
